import { ColumnType } from 'antd/lib/table';
import { ReactNode } from 'react';
import { IConfigField } from '../auto-table/model';
import { IFilterItem } from '../filter-group';
import { PaginationTable } from '../pagination-table';
import { IPagination, ITableLocale } from '../table';

interface IAutoAntTableColumn<T> extends ColumnType<T> {
  showTooltip?: boolean;
}

interface IProps<T> {
  loading: boolean;
  dataSource: readonly T[];
  columns: IAutoAntTableColumn<T>[];
  defaultPageSize?: number;
  className?: string;
  rowSelection?: {
    selectedRowKeys: string[] | number[];
    onChange: (keys: readonly string[] | readonly number[]) => void;
    hideSelectAll?: boolean;
    getCheckboxProps?: (record?: T) => { disabled?: boolean; tips?: string };
  };
  scroll?: { x?: number | string; y?: number | string };

  pagination?: false;
  showTotal?: true | ((total: number, pagination: IPagination) => ReactNode);
  locale?: ITableLocale;
  configFilter?: {
    filters: readonly IFilterItem<T>[];
    onChange?: (values: any, changeFiled: string, filterList: T[]) => void;
  };
}

export const AutoAntTable = <T,>(props: IProps<T>) => {
  const {
    rowSelection,
    dataSource,
    scroll,
    pagination,
    showTotal,
    defaultPageSize,
    locale,
    configFilter,
  } = props;

  const columnsConfig = props.columns || [];
  const configFields = columnsConfig.map((column) => {
    const configs: IConfigField<T> = {
      key: String(column.dataIndex || column.key || ''),
      name: column.title as string,

      tableExtension: {
        width: column.width,
        fixed: column.fixed,
        showTooltip: column.showTooltip,
        sortable: !!column.defaultSortOrder || !!column.sorter,
        sorter: typeof column.sorter === 'function' ? column.sorter : undefined,
        renderRow: column.render
          ? (_key, val: T, record, field, index) => {
              return column.render?.(val, record, index);
            }
          : undefined,
      },
    };
    return configs;
  });

  const getRowSelectionConfig = () => {
    if (!rowSelection) {
      return;
    }

    return {
      fixed: !!columnsConfig?.[0]?.fixed || undefined,
      selectedRowKeys: rowSelection.selectedRowKeys,
      disabledSelectAll: rowSelection.hideSelectAll,
      getCheckboxProps: rowSelection.getCheckboxProps,
      onChange: (selectedRowKeys: readonly string[] | readonly number[]) => {
        rowSelection.onChange(selectedRowKeys);
      },
    };
  };

  return (
    <PaginationTable
      className={props.className}
      loading={props.loading}
      list={dataSource}
      configFields={configFields}
      tableExtension={{
        isStickyHeader: true,
        scroll,
        rowSelection: getRowSelectionConfig(),
        locale,
      }}
      pagination={pagination}
      defaultPageSize={defaultPageSize}
      showTotal={showTotal}
      configFilter={
        configFilter && {
          ...configFilter,
        }
      }
    />
  );
};
